<template>
  <div class="flex-1 flex flex-col">
    <!-- Actions -->
    <Actions
      :current-month="calendar.meta.month"
      :disabled="loading"
      @set-weekly-view="$emit('set-weekly-view')"
      @set-month="$emit('set-month', arguments[0])"
      @prev-month="$emit('prev-month')"
      @next-month="$emit('next-month')"
      @today="$emit('today', calendar.meta.now)"
    >
      <h6 class="my-auto ml-2 font-medium">
        {{ calendar.meta.focusedDate.format('YYYY MMMM') }}
      </h6>
      <div class="flex items-center ml-auto mr-4">
        <span class="inline-block mr-2">View</span>
        <label class="relative">
          <input
            :checked="assignedToMe"
            :value="false"
            type="radio"
            name="assigned"
            class="field opacity-0 absolute inset-0 w-full h-full"
            @change="$emit('update:assigned-to-me', false)"
          />
          <span
            class="inline-block px-3 py-2 bg-white leading-6 rounded-l field-checked:bg-blue-600 field-checked:text-white transition-colors duration-75"
          >
            All
          </span>
        </label>
        <label class="relative">
          <input
            :checked="assignedToMe"
            :value="true"
            type="radio"
            name="assigned"
            class="field opacity-0 absolute inset-0 w-full h-full"
            @change="$emit('update:assigned-to-me', true)"
          />
          <span
            class="inline-block px-3 py-2 bg-white leading-6 rounded-r field-checked:bg-blue-600 field-checked:text-white transition-colors duration-75"
          >
            Assigned to me
          </span>
        </label>
      </div>
    </Actions>

    <!-- Calendar Header -->
    <CalHeader :week-days="calendar.header" />

    <!-- Calendar Body -->
    <div
      v-if="loading"
      key="loading_monthly"
      class="flex flex-1 border-t border-gray-600 text-center"
    >
      <Loader class="m-auto" />
    </div>
    <template v-else>
      <div
        v-for="week in calendar.weeks"
        :key="`monthly_${week.number}`"
        class="flex-1 flex border-t border-gray-600"
      >
        <CalDate
          v-for="(day, index) in week.days"
          :key="`${week.number}_${index}`"
          v-bind="{ week: week.number, day: day.day, meta: day.meta }"
          @set-date="$emit('set-date', arguments[0])"
        >
          <div
            v-if="tasksByDate[day.day.format('YYYY-MM-DD')]"
            class="rounded bg-green-500 py-1 px-2 my-auto"
          >
            {{ tasksByDate[day.day.format('YYYY-MM-DD')].length }} Tasks
          </div>

          <div
            v-if="campaignsByStartingDate[day.day.format('YYYY-MM-DD')]"
            class="rounded-l bg-green-200 text-green-600 border-l-4 border-t border-b border-green-600 py-1 px-2 my-auto"
          >
            {{ campaignsByStartingDate[day.day.format('YYYY-MM-DD')].length }} Campaigns to start
          </div>

          <div
            v-if="campaignsByEndingDate[day.day.format('YYYY-MM-DD')]"
            class="rounded-r bg-red-200 text-red-600 border-r-4 border-red-600 border-t border-b text-right py-1 px-2 my-auto"
          >
            {{ campaignsByEndingDate[day.day.format('YYYY-MM-DD')].length }} Campaigns to end
          </div>

          <div
            v-if="productionOrdersByDate[day.day.format('YYYY-MM-DD')]"
            class="rounded bg-yellow-200 py-1 px-2 my-auto border border-yellow-500"
          >
            {{ productionOrdersByDate[day.day.format('YYYY-MM-DD')].length }} Production Orders
          </div>
        </CalDate>
      </div>
    </template>
  </div>
</template>

<script>
import Actions from './monthly/Actions.vue';
import CalHeader from './monthly/Header.vue';
import CalDate from './monthly/Date.vue';

export default {
  name: 'Monthly',

  components: {
    Actions,
    CalHeader,
    CalDate,
  },

  props: {
    calendar: {
      type: Object,
      required: true,
    },
    tasks: {
      type: Array,
      default: () => [],
    },
    campaigns: {
      type: Array,
      default: () => [],
    },
    productionOrders: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    assignedToMe: Boolean,
  },

  computed: {
    tasksByDate() {
      const tasksByDate = {};

      this.tasks
        .filter((task) => task.dueAt)
        .forEach((task) => {
          const parts = task.dueAt.split(' ');
          const date = parts[0];

          if (typeof tasksByDate[date] === 'undefined') tasksByDate[date] = [];
          tasksByDate[date].push(task);
        });

      return tasksByDate;
    },
    campaignsByStartingDate() {
      const campaignsByStartingDate = {};

      this.campaigns
        .filter((campaign) => ['both', 'start'].indexOf(campaign.datesVisibleOnCalendar) !== -1)
        .filter((campaign) => campaign.dateStart)
        .forEach((campaign) => {
          const date = campaign.dateStart;

          if (typeof campaignsByStartingDate[date] === 'undefined')
            campaignsByStartingDate[date] = [];
          campaignsByStartingDate[date].push(campaign);
        });

      return campaignsByStartingDate;
    },
    campaignsByEndingDate() {
      const campaignsByEndingDate = {};

      this.campaigns
        .filter((campaign) => ['both', 'end'].indexOf(campaign.datesVisibleOnCalendar) !== -1)
        .filter((campaign) => campaign.dateEnd)
        .forEach((campaign) => {
          const date = campaign.dateEnd;

          if (typeof campaignsByEndingDate[date] === 'undefined') campaignsByEndingDate[date] = [];
          campaignsByEndingDate[date].push(campaign);
        });

      return campaignsByEndingDate;
    },
    productionOrdersByDate() {
      const productionOrdersByDate = {};

      this.productionOrders.forEach((productionOrder) => {
        const parts = productionOrder.deliveryAt.split(' ');
        const date = parts[0];

        if (typeof productionOrdersByDate[date] === 'undefined') productionOrdersByDate[date] = [];
        productionOrdersByDate[date].push(productionOrder);
      });

      return productionOrdersByDate;
    },
  },
};
</script>
